import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPage, fetchGallery } from '../lib/cms';
import GalleryModal from '../components/GalleryModal';
import { useScrollReveal } from '../hooks/useScrollReveal';

const serviceImages = {
  asfalt: 'https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/asfalt-arbejde.jpg',
  roerlaegning: 'https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/roerlaegning.jpg',
  entreprenoerarbejde: 'https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/entreprenoerarbejde.jpg',
  mandskabsudlejning: 'https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/mandskabsudlejning.jpg'
};

const serviceHeadings = {
  asfalt: 'Kvalitetsbevidst asfaltarbejde på Sydfyn',
  roerlaegning: 'Professionel rørlægning og afvanding',
  entreprenoerarbejde: 'Effektivt og præcist entreprenørarbejde',
  mandskabsudlejning: 'Pålidelig og fleksibel mandskabsudlejning'
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [contentRef, contentRevealed] = useScrollReveal();
  const [galleryRef, galleryRevealed] = useScrollReveal();
  const [ctaRef, ctaRevealed] = useScrollReveal();

  // Lightbox state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  useEffect(() => {
    async function loadServiceData() {
      setLoading(true);
      const pageSlug = `page-${slug}`;
      const page = await fetchPage(pageSlug);
      
      if (!page) {
        // Redirect to 404 if page doesn't exist
        navigate('/404', { replace: true });
        return;
      }

      setPageData(page);

      if (page.gallery_slug) {
        const gallery = await fetchGallery(page.gallery_slug);
        setGalleryData(gallery);
      } else {
        setGalleryData(null);
      }

      setLoading(false);
    }

    loadServiceData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-sand-50">
        <div className="text-center">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-safety-orange"></i>
          <p className="mt-4 text-sm font-semibold text-asphalt-600">Henter ydelsesinfo...</p>
        </div>
      </div>
    );
  }

  const metaTitle = pageData?.meta_title || `${pageData?.title} - JE Entreprenør`;
  const metaDesc = pageData?.meta_description || `Læs mere om vores ydelser inden for ${pageData?.title} på Sydfyn.`;
  const heroTitle = pageData?.hero_title || pageData?.title;
  const heroSubtitle = pageData?.hero_subtitle || '';
  const heroImage = serviceImages[slug] || 'https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/hero-entreprenoer.jpg';

  const handleOpenLightbox = (index) => {
    setCurrentImgIndex(index);
    setIsModalOpen(true);
  };

  const handlePrevImage = () => {
    if (!galleryData?.images) return;
    setCurrentImgIndex((prev) => (prev === 0 ? galleryData.images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (!galleryData?.images) return;
    setCurrentImgIndex((prev) => (prev === galleryData.images.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={heroImage} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-asphalt-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-35">
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${heroImage.replace('https://assets.auxodev.dk/', '')}`}
            alt={pageData?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-asphalt-950/80" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
            {heroTitle}
          </h1>
          <p className="mt-4 mx-auto max-w-2xl text-base text-asphalt-200">
            {heroSubtitle}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section 
        className="py-16 bg-white"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-asphalt max-w-3xl">
            <h2 className="font-display text-2xl font-black uppercase tracking-tight text-asphalt-950 mb-6">
              {serviceHeadings[slug] || `Professionelt arbejde inden for ${pageData?.title}`}
            </h2>
            <div 
              className="text-base text-asphalt-700 space-y-6 leading-relaxed [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-asphalt-950 [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:font-display [&_h3]:uppercase [&_h3]:tracking-wider [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-2 [&_li]:pl-1 [&_strong]:font-bold [&_strong]:text-asphalt-950"
              dangerouslySetInnerHTML={{ __html: pageData?.content || '' }}
            />
          </div>

          {/* Local Focus Box */}
          <div className="mt-12 border border-black bg-asphalt-950 p-6 sm:p-8 text-white">
            <div className="flex items-start gap-4">
              <div className="text-safety-orange pt-1">
                <i className="fa-solid fa-location-dot text-xl"></i>
              </div>
              <div>
                <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider">Lokalt Sydfyn-fokus</h3>
                <p className="mt-2 text-sm text-asphalt-200 leading-relaxed">
                  Vi udfører {pageData?.title.toLowerCase()} på hele Sydfyn – herunder Svendborg, Skårup, Nyborg, Faaborg-Midtfyn samt Langeland og Tåsinge. Vi kender de lokale forhold og leverer altid et holdbart resultat tilpasset det sydfynske terræn.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryData && galleryData.images && galleryData.images.length > 0 && (
        <section 
          className="py-16 bg-sand-100 border-t border-b border-asphalt-100"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-asphalt-900 sm:text-3xl">
                Udført arbejde - {pageData?.title}
              </h2>
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {galleryData.images.map((imgUrl, index) => (
                <button
                  key={index}
                  onClick={() => handleOpenLightbox(index)}
                  className="group relative h-64 w-full overflow-hidden rounded-xl border border-asphalt-200 bg-white shadow-sm hover:border-safety-orange transition-all duration-300 cursor-pointer"
                  aria-label={`Vis billede ${index + 1} i stor visning`}
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`${pageData?.title} projektbillede ${index + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-asphalt-950/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-asphalt-900 shadow-md">
                      <i className="fa-solid fa-magnifying-glass-plus text-lg"></i>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section 
        className="py-16 bg-white"
      >
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-asphalt-900 sm:text-3xl">
            Skal vi hjælpe dig med dit næste projekt?
          </h2>
          <p className="mt-4 mx-auto max-w-2xl text-base text-asphalt-600">
            Kontakt os i dag for en uforpligtende snak og et uforpligtende tilbud på {pageData?.title.toLowerCase()} på Sydfyn.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-lg bg-safety-orange px-6 py-3 text-sm font-bold text-white hover:bg-safety-orange-hover hover:-translate-y-1 transition-all duration-200"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href="tel:+4551172145"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-sm font-bold text-asphalt-900 hover:text-safety-orange hover:-translate-y-1 transition-all duration-200"
            >
              <i className="fa-solid fa-phone mr-2"></i>
              Ring på 51 17 21 45
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {galleryData && (
        <GalleryModal
          images={galleryData.images}
          currentIndex={currentImgIndex}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onPrev={handlePrevImage}
          onNext={handleNextImage}
        />
      )}
    </>
  );
}
